import { useEffect, useState } from 'react'
import { ErrorApi } from '../lib/api'

type Opcion = { codigo: string; etiqueta: string }

type Props = {
  etiqueta: string
  valor: string
  guardar: (valor: string) => Promise<unknown>
  tipo?: 'text' | 'tel' | 'email' | 'date' | 'number'
  opciones?: Opcion[]
  ayuda?: string
  marcador?: string
  obligatorio?: boolean
  bloqueado?: boolean
  enfocar?: boolean
  /** Dato comprobado y correcto: se enmarca en verde y se dice que vale. */
  valido?: boolean
  /** Lo que se enseña en verde cuando el dato es válido. */
  textoValido?: string
}

/**
 * Campo con autoguardado: al salir del campo (o al elegir en un desplegable)
 * se manda al servidor y se enseña si ha entrado o qué falla. El comercial
 * está al teléfono: no puede estar pendiente de pulsar "guardar".
 */
export function Campo({
  etiqueta,
  valor,
  guardar,
  tipo = 'text',
  opciones,
  ayuda,
  marcador,
  obligatorio,
  bloqueado,
  enfocar,
  valido,
  textoValido,
}: Props) {
  const [texto, setTexto] = useState(valor)
  const [estado, setEstado] = useState<'quieto' | 'guardando' | 'guardado' | 'error'>('quieto')
  const [error, setError] = useState<string | null>(null)

  // Si el valor cambia por fuera (otro comercial en la misma ficha), se refleja.
  useEffect(() => {
    setTexto(valor)
  }, [valor])

  const enviar = async (nuevo: string) => {
    if (nuevo === valor) return
    setEstado('guardando')
    setError(null)
    try {
      await guardar(nuevo)
      setEstado('guardado')
      setTimeout(() => setEstado('quieto'), 1500)
    } catch (e) {
      setEstado('error')
      setError(e instanceof ErrorApi ? e.message : 'No se ha podido guardar')
      setTexto(valor)
    }
  }

  const clases =
    'w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none transition dark:bg-slate-950 ' +
    (estado === 'error'
      ? 'border-red-400 focus:ring-2 focus:ring-red-100'
      : valido
        ? 'border-marca-500 bg-marca-50/40 focus:ring-2 focus:ring-marca-100 dark:bg-marca-700/10'
        : 'border-slate-200 focus:border-marca-500 focus:ring-2 focus:ring-marca-100 dark:border-slate-700')

  return (
    <label className="block">
      <span className="mb-1 flex items-center gap-2 text-xs font-semibold text-slate-500">
        {etiqueta}
        {obligatorio && <span className="text-red-500">*</span>}
        {estado === 'guardando' && <span className="text-slate-400">guardando…</span>}
        {estado === 'guardado' && <span className="text-marca-600">guardado</span>}
        {valido && estado === 'quieto' && (
          <span className="font-bold text-marca-600">✓ {textoValido ?? 'válido'}</span>
        )}
      </span>

      {opciones ? (
        <select
          value={texto}
          disabled={bloqueado}
          onChange={(e) => {
            setTexto(e.target.value)
            void enviar(e.target.value)
          }}
          className={clases}
        >
          <option value="">— elegir —</option>
          {opciones.map((opcion) => (
            <option key={opcion.codigo} value={opcion.codigo}>
              {opcion.etiqueta}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={tipo}
          value={texto}
          disabled={bloqueado}
          autoFocus={enfocar}
          placeholder={marcador}
          onChange={(e) => setTexto(e.target.value)}
          onBlur={() => void enviar(texto)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
          }}
          className={clases}
        />
      )}

      {error ? (
        <span className="mt-1 block text-xs text-red-600">{error}</span>
      ) : ayuda ? (
        <span className={`mt-1 block text-xs ${valido ? 'text-marca-600' : 'text-slate-400'}`}>
          {ayuda}
        </span>
      ) : null}
    </label>
  )
}
